import type { DatePickerProps } from "antd";
import { DatePicker as AntDatePicker } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useRef } from "react";

dayjs.extend(customParseFormat);

const DATE_FORMATS = ["DDMMYYYY", "DD/MM/YYYY", "YYYY-MM-DD"];

export interface CustomDatePickerProps
  extends Omit<DatePickerProps, "format" | "onChange" | "value"> {
  format?: string | string[];
  value?: string | Dayjs | null | number;
  onChange?: (value: string | Dayjs | null) => void;
}

const DatePicker: React.FC<CustomDatePickerProps> = ({
  format = DATE_FORMATS,
  value,
  onChange,
  ...restProps
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Convert any value type to Dayjs object
  const getDayjsValue = (val: string | Dayjs | null | undefined | number): Dayjs | null => {
    if (!val) return null;
    if (dayjs.isDayjs(val)) return val;

    for (const fmt of Array.isArray(format) ? format : [format]) {
      const parsed = dayjs(val, fmt);
      if (parsed.isValid()) {
        return parsed;
      }
    }

    const parsed = dayjs(val);
    return parsed.isValid() ? parsed : null;
  };

  // Add event listener to format input as user types
  useEffect(() => {
    // Function to handle input formatting
    const formatDateInput = (input: HTMLInputElement) => {
      const value = input.value;
      const selectionStart = input.selectionStart || 0;

      // Remove any non-digits except slashes
      let formattedValue = value.replace(/[^\d/]/g, "");

      // Track cursor position
      let newPosition = selectionStart;
      let addedChars = 0;

      // Format with slashes
      if (formattedValue.length > 0) {
        // Add slash after day (position 2)
        if (formattedValue.length >= 2 && !/\//.test(formattedValue.substring(0, 3))) {
          if (formattedValue.length === 2) {
            formattedValue += "/";
            if (selectionStart === 2) addedChars++;
          } else {
            formattedValue = `${formattedValue.substring(0, 2)}/${formattedValue.substring(2)}`;
            if (selectionStart > 2) addedChars++;
          }
        }

        // Add slash after month (position 5)
        if (formattedValue.length >= 5) {
          const parts = formattedValue.split("/");
          if (parts.length === 2 && parts[1].length >= 2) {
            if (parts[1].length === 2) {
              formattedValue = `${parts[0]}/${parts[1]}/`;
              if (selectionStart === 5) addedChars++;
            } else {
              formattedValue = `${parts[0]}/${parts[1].substring(0, 2)}/${parts[1].substring(2)}`;
              if (selectionStart > 5) addedChars++;
            }
          }
        }
      }

      // Limit to 10 chars (DD/MM/YYYY)
      if (formattedValue.length > 10) {
        formattedValue = formattedValue.substring(0, 10);
      }

      // Update input if value changed
      if (formattedValue !== value) {
        // Calculate new cursor position
        newPosition = selectionStart + addedChars;

        // Set the new value
        input.value = formattedValue;

        // Restore cursor position
        setTimeout(() => {
          input.setSelectionRange(newPosition, newPosition);
        }, 0);

        // Trigger change event
        const event = new Event("input", { bubbles: true });
        input.dispatchEvent(event);
      }
    };

    const inputElement = containerRef.current?.querySelector(
      ".ant-picker-input input",
    ) as HTMLInputElement | null;

    if (!inputElement) return;

    // Add input event handler
    const handleInput = (e: Event) => {
      formatDateInput(e.target as HTMLInputElement);
    };

    inputElement.addEventListener("input", handleInput);

    // Cleanup when component unmounts
    return () => {
      inputElement.removeEventListener("input", handleInput);
    };
  }, []);

  return (
    <div ref={containerRef}>
      <AntDatePicker
        format={format}
        value={getDayjsValue(value)}
        onChange={onChange}
        placeholder={
          typeof format === "string" ? format : Array.isArray(format) ? format[0] : "DD/MM/YYYY"
        }
        {...restProps}
      />
    </div>
  );
};

export default DatePicker;
